/**
 * Shared generation functions for both flat HTML export and 11ty export.
 * Extracted from exporter.js to avoid duplication.
 *
 * Exports:
 *   Core generators: assignExportIds, generateRootCSS, generateLocalCSS,
 *                    generateCriticalCSS, generatePageBundles,
 *                    generateExportScripts, cleanSectionHTML, applyFormConfig
 *   Helpers:         escapeHTML, escapeForScript, escapeRegExp, camelToKebab
 *   11ty utilities:  stripAstroCidSelectors, safeYAMLValue, generateSlug, wrapInRawTags
 */

import * as csstree from '../vendor/csstree.esm.js';
import { sanitize } from '../core/sanitizer.js';
import { applyPreviewElementAttributeOverrides } from '../shared/element-attribute-overrides.js';
import { escapeHTML } from '../shared/html-escape.js';
export { escapeHTML };
import { stripAllBuilderAttrs } from '../editing/editable-utils.js';
import {
  applyEntranceAttrs,
  ENTRANCE_ATTR,
  ENTRANCE_DELAY_ATTR,
  ENTRANCE_REVEALED_CLASS,
  ENTRANCE_ARMED_CLASS
} from '../shared/entrance-presets.js';
import { assembleEntranceCSS } from '../shared/entrance-static-css.js';
import { stateEmitsEntrance } from './scroll-animation-shim.js';
import { normalizeLogoSlots } from '../editing/logo-normalizer.js';
import {
  buildImageEntryIndex,
  isLogoImage,
  resolveImageEntry
} from './image-shortcode-rewriter.js';
import {
  hasImagePositionOverride,
  hasImageHoverOverride,
  isValidImageHoverPreset,
  hasImageEffectOverride,
  isValidImageEffect,
  formatImagePct,
  formatImageScale
} from '../editing/image-runtime-css.js';
import {
  assembleButtonCSS,
  assembleFormCSS,
  assembleSectionOverrideCSS,
  buildDecorativeBleedCSS,
  INLINE_TEXT_COLOR_CSS,
  resolveFontSizeCSSValue
} from '../renderer/css-assembler.js';
import { getButtonEffectJS, BUBBLY_BUTTON_JS } from '../renderer/animation-injector.js';
import { DEFAULT_BUTTON_SELECTOR } from '../shared/button-vocabulary.js';
import {
  rewriteCssAssetUrlsPreferVendored,
  getVendoredAssetMapSync,
  normalizeSectionTypography,
  stripTemplateComponentRules,
  resolveComponentStripPlan,
  TEMPLATE_BUTTON_CLASSES,
  TEMPLATE_CARD_CLASSES,
  TEMPLATE_INPUT_CLASSES,
  TEMPLATE_PILL_CLASSES
} from '../shared/template-support-css.js';
import { assembleTemplateFidelityCSS } from '../shared/template-fidelity-css.js';
import { assembleMotionStaticCSS } from '../shared/motion-static-css.js';
import { applyIconTintToDOM } from '../shared/icon-tint.js';
import { scopeCSS } from '../shared/css-scoper.js';
import { parseFirstFont } from '../shared/font-utils.js';
import { normalizeThemeTokens } from '../shared/theme-tokens.js';
import { collectInlineFontFamilies } from '../shared/font-catalog.js';
import { buildFontRuntimeCSS } from '../editing/font-runtime-css.js';
import {
  TEMPLATE_SUPPORT_MARKER_PREFIX,
  TEMPLATE_SUPPORT_ATTR,
  scopeTemplateSupportBlocks,
  templateSupportAttrValue,
  sectionNeedsTemplateSupportCSS
} from '../shared/template-support-css.js';
import { isSharedCategory } from '../shared/shared-section-types.js';
import { buildExportIdPlan } from './export-id-plan.js';
import { applyIdRenamesToSectionDOM } from './dom-id-rewriter.js';
import { rewriteCssIds } from './css-id-rewriter.js';
import { buildFacadeIIFE, sectionUsesDocumentScope } from '../shared/section-js-facade.js';
import { behaviourIsShipped, behaviourStampsInHtml } from '../shared/behaviour-contract.js';
import { assembleBehaviourJS, behaviourBindJS } from '../behaviours/behaviour-loader.js';
import { detectMultiItemGroups } from '../editing/section-group-detection.js';
import { normalizeDomain } from '../shared/client-info.js';

const ASTRO_CID_ATTR_PREFIX = 'data-astro-cid-';

// ---------------------------------------------------------------------------
// Core generators (extracted from exporter.js)
// ---------------------------------------------------------------------------

/**
 * Assign unique export IDs to all section instances across all pages.
 * First instance of a catalogKey gets the raw sectionId.
 * Subsequent duplicates get sectionId + "-2", "-3", etc.
 *
 * @param {object} state
 * @param {object} [options]
 * @param {boolean} [options.normalizeTemplateIds=false] - Strip tpl-{site}- prefixes
 * from exported IDs (11ty-only mode).
 * @returns {Map<string, string>} instanceId → exportId
 */
export function assignExportIds(state, options = {}) {
  // Thin delegate: the id plan (export-id-plan.js) is the single allocator
  // for every export surface. Legacy mode reproduces the historical behavior
  // here bug-for-bug; see that module's header.
  return buildExportIdPlan(state, options).exportIdMap;
}

function buildTemplateExportIdMap(state, exportIdMap) {
  const bySectionId = new Map();
  const allInstances = collectAllSectionInstances(state);

  for (const section of allInstances) {
    const sectionId = section.sectionId || section.catalogKey || section.instanceId;
    const exportId = exportIdMap.get(section.instanceId);
    if (!sectionId || !exportId) continue;

    if (!bySectionId.has(sectionId)) bySectionId.set(sectionId, []);
    const ids = bySectionId.get(sectionId);
    if (!ids.includes(exportId)) ids.push(exportId);
  }

  return bySectionId;
}

function templateSelectorForSection(sectionId, templateExportIds) {
  const ids = templateExportIds.get(sectionId);
  if (!ids || ids.length === 0) return `#${sectionId}`;
  if (ids.length === 1) return `#${ids[0]}`;
  return `:is(${ids.map((id) => `#${id}`).join(', ')})`;
}

function collectAllSectionInstances(state) {
  const allInstances = [];
  if (state?.sharedSections?.navigation) allInstances.push(state.sharedSections.navigation);
  for (const page of state?.pages || []) {
    for (const section of page?.sections || []) {
      allInstances.push(section);
    }
  }
  if (state?.sharedSections?.footer) allInstances.push(state.sharedSections.footer);
  return allInstances;
}

function rewriteTemplateSelectorsForSection(css, sectionId, templateExportIds) {
  const selector = templateSelectorForSection(sectionId, templateExportIds);
  // AST-located rewrite (A3): byte-preserving splices at IdSelector /
  // AttributeSelector positions, immune to `content: "#id"` strings and
  // escaped selectors. Legacy regex only as the parse-failure fallback.
  const rewritten = rewriteCssIds(css, {
    idSelectorMap: { [sectionId]: selector },
    attrSectionMap: { [sectionId]: selector }
  });
  if (rewritten !== null) return rewritten;
  const attrRe = new RegExp(escapeRegExp(`[data-pb-section="${sectionId}"]`), 'g');
  const idRe = new RegExp(`#${escapeRegExp(sectionId)}(?=[\\s.:\\[,>+~#\\{\\)]|$)`, 'g');
  return css.replace(attrRe, selector).replace(idRe, selector);
}

function rewriteResolvedAssetUrls(css, resolvedMap) {
  if (!css || !(resolvedMap instanceof Map) || resolvedMap.size === 0) return css;
  let rewritten = css;
  for (const [originalUrl, entry] of resolvedMap) {
    const nextUrl = entry?.htmlPath || entry?.localPath;
    if (!nextUrl || !rewritten.includes(originalUrl)) continue;
    rewritten = rewritten.split(originalUrl).join(nextUrl);
  }
  return rewritten;
}

function shouldStripSectionComponentRules(section, flag, options = {}) {
  if (!flag) return false;
  if (!section || isSharedCategory(section.category)) return false;

  const primaryDomain =
    typeof options.sourceTemplateDomain === 'string' ? options.sourceTemplateDomain.trim() : '';
  if (!primaryDomain) return true;

  const sectionDomain =
    typeof section.templateSiteName === 'string' ? section.templateSiteName.trim() : '';
  return sectionDomain !== primaryDomain;
}

function prepareSectionCSSForExport(section, css, options = {}) {
  let nextCSS = css;
  if (shouldStripSectionComponentRules(section, options.stripButtons, options)) {
    nextCSS = stripTemplateComponentRules(nextCSS, TEMPLATE_BUTTON_CLASSES);
  }
  if (shouldStripSectionComponentRules(section, options.stripCards, options)) {
    nextCSS = stripTemplateComponentRules(nextCSS, TEMPLATE_CARD_CLASSES);
  }
  if (shouldStripSectionComponentRules(section, options.stripInputs, options)) {
    nextCSS = stripTemplateComponentRules(nextCSS, TEMPLATE_INPUT_CLASSES);
  }
  if (shouldStripSectionComponentRules(section, options.stripPills, options)) {
    nextCSS = stripTemplateComponentRules(nextCSS, TEMPLATE_PILL_CLASSES);
  }
  return nextCSS;
}

function selectorAttrName(node) {
  if (!node || !node.name) return '';
  if (typeof node.name.name === 'string') return node.name.name;
  if (typeof node.name.value === 'string') return node.name.value;
  if (typeof node.name === 'string') return node.name;
  return '';
}

function normalizeSelectorChildren(selectorNode) {
  if (!selectorNode?.children) return;

  while (!selectorNode.children.isEmpty) {
    const firstNode = selectorNode.children.first;
    if (firstNode?.type === 'Combinator') {
      selectorNode.children.remove(selectorNode.children.head);
      continue;
    }
    break;
  }

  while (!selectorNode.children.isEmpty) {
    const lastNode = selectorNode.children.last;
    if (lastNode?.type === 'Combinator') {
      selectorNode.children.remove(selectorNode.children.tail);
      continue;
    }
    break;
  }

  let prevWasCombinator = false;
  selectorNode.children.forEach((child, item) => {
    const isCombinator = child?.type === 'Combinator';
    if (isCombinator && prevWasCombinator) {
      selectorNode.children.remove(item);
      return;
    }
    prevWasCombinator = isCombinator;
  });
}

function cleanupEmptySelectors(ast) {
  csstree.walk(ast, {
    visit: 'Selector',
    reverse: true,
    enter(node, item, list) {
      normalizeSelectorChildren(node);
      if (node.children?.isEmpty && list && item) {
        list.remove(item);
      }
    }
  });
}

function removeAstroCidSelectorNodes(ast) {
  csstree.walk(ast, {
    visit: 'AttributeSelector',
    enter(node, item, list) {
      const name = selectorAttrName(node);
      if (name.startsWith(ASTRO_CID_ATTR_PREFIX) && list && item) {
        list.remove(item);
      }
    }
  });

  cleanupEmptySelectors(ast);

  csstree.walk(ast, {
    visit: 'PseudoClassSelector',
    reverse: true,
    enter(node, item, list) {
      if (node.children?.forEach) {
        node.children.forEach((child, childItem) => {
          if (child?.type === 'SelectorList' && child.children?.isEmpty) {
            node.children.remove(childItem);
          }
        });
      }
      if (node.children?.isEmpty && list && item) {
        list.remove(item);
      }
    }
  });

  cleanupEmptySelectors(ast);

  csstree.walk(ast, {
    visit: 'Rule',
    reverse: true,
    enter(node, item, list) {
      if (node.prelude?.type === 'SelectorList' && node.prelude.children?.isEmpty && list && item) {
        list.remove(item);
      }
    }
  });
}

/**
 * Remove `[data-astro-cid-*]` selectors from CSS using css-tree.
 * Invalid/empty selectors/rules introduced by removal are pruned.
 */
export function stripAstroCidSelectors(css) {
  if (!css || !css.includes('data-astro-cid-')) return css || '';

  let ast;
  try {
    ast = csstree.parse(css);
  } catch (err) {
    console.warn('[export] stripAstroCidSelectors parse failed:', err.message);
    return css;
  }

  removeAstroCidSelectorNodes(ast);

  try {
    return csstree.generate(ast);
  } catch (err) {
    console.warn('[export] stripAstroCidSelectors generate failed:', err.message);
    return css;
  }
}

function sectionKey(section) {
  return section?.catalogKey || section?.sectionId || section?.instanceId || '';
}

/**
 * The project's button selector for an export path. Derived through the same
 * `resolveComponentStripPlan` the preview uses, so an exported site and the
 * canvas cannot disagree about what a button is. Without `options.templateCSS`
 * there is no project CSS to derive from, and the CodeStitch default is both
 * the honest answer and the historical one.
 */
function resolveButtonSelector(state, options = {}) {
  const templateCSS = typeof options.templateCSS === 'string' ? options.templateCSS : '';
  if (!templateCSS) return DEFAULT_BUTTON_SELECTOR;
  return resolveComponentStripPlan(state, templateCSS).buttonSelector;
}

/**
 * Generate root.css: theme variables + global base styles + font-face + button styles.
 *
 * `options.templateCSS` is what lets the button rules reach a template that does
 * not use the CodeStitch class names. Omitted → the CodeStitch default, i.e.
 * exactly the historical output.
 */
export function generateRootCSS(state, globalCSS, fontFaceCss = '', options = {}) {
  const parts = [];
  if (fontFaceCss) parts.push(fontFaceCss);

  // Resolve theme fonts upfront so we can emit the deterministic --pb-*-font
  // vars consumed by normalizeSectionTypography alongside the rest of :root.
  const bodyFontName = parseFirstFont(state.font) || 'Roboto';
  const headingFontName = parseFirstFont(state.headingFont) || bodyFontName;

  // Theme variables
  const vars = Object.entries(
    normalizeThemeTokens(state.theme, { preserveExisting: true, includeDerived: true })
  )
    .map(([key, value]) => `  ${key}: ${value};`)
    .join('\n');
  const pbFontVars = [
    `  --pb-body-font: '${bodyFontName}', 'Roboto', Arial, sans-serif;`,
    `  --pb-heading-font: '${headingFontName}', 'Roboto', Arial, sans-serif;`
  ].join('\n');
  parts.push(`:root {\n${vars}\n${pbFontVars}\n}`);

  // Accessibility fallback for exported skip link.
  // Placed before template CSS so templates can override it when desired.
  parts.push(`.skip {
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
}
.skip:focus {
  left: 1rem;
  top: 1rem;
  width: auto;
  height: auto;
  overflow: visible;
  z-index: 99999;
  padding: 0.75rem 1rem;
  background: var(--primary, #000);
  color: #fff;
  text-decoration: none;
}`);

  // Global base styles
  if (globalCSS) parts.push(globalCSS);

  // Font — reuse the names already resolved at the top for the :root vars.
  if (bodyFontName && bodyFontName !== 'Roboto') {
    parts.push(`body { font-family: '${bodyFontName}', 'Roboto', Arial, sans-serif; }`);
  } else {
    parts.push(`body { font-family: 'Roboto', Arial, sans-serif; }`);
  }

  // Heading font (if different from body font). Skipped while the fonts are
  // only DERIVED from the template (`fontSource === 'template'`): the rule
  // also forces a weight, and re-declaring a template's own family with
  // `font-weight: 700` bolded every heading of a site designed at 300. Same
  // gate as the preview renderer's `pb-heading-font` block, so an export
  // matches the canvas.
  if (headingFontName && headingFontName !== bodyFontName && state.fontSource !== 'template') {
    const hWeight = state.headingFontWeight || '700';
    parts.push(
      `h1,h2,h3,h4,h5,h6,.cs-title,.cs-h3,.cs-topper { font-family: '${headingFontName}', 'Roboto', Arial, sans-serif; font-weight: ${hWeight}; }`
    );
  }

  // Button styles
  if (state.buttonStyles) {
    const btnCSS = assembleButtonCSS(state.buttonStyles, resolveButtonSelector(state, options));
    if (btnCSS) parts.push(btnCSS);
  }

  if (state.formStyles) {
    const formCSS = assembleFormCSS(state.formStyles);
    if (formCSS) parts.push(formCSS);
  }

  // Inline text color utility classes used by the editor formatter
  parts.push(INLINE_TEXT_COLOR_CSS);
  parts.push(buildFontRuntimeCSS(collectInlineFontFamilies(state)));

  // Builder entrance animations (EA-2). PRESENCE-GATED: a project with no
  // preset set ships none of this, which is what keeps the version bump from
  // changing any existing export's bytes.
  //
  // root.css, deliberately not local.css: the 11ty exporter truncates local.css
  // to the home page's sections, so a project whose ONLY entrance override sits
  // on an interior page would ship stamped markup with no stylesheet to animate
  // it. root.css is the cross-page sheet, and this rule set is page-agnostic.
  // It is also NOT part of critical CSS — it must not paint before the runtime
  // can arm.
  if (stateEmitsEntrance(state)) parts.push(assembleEntranceCSS());

  return parts.join('\n\n');
}

/**
 * Generate button effect JS for export. Combines the theme-wide effect (if any)
 * with the bubbly-button trigger, which is idempotent and safe to ship in every
 * export — it no-ops when no `.bubbly-button` elements exist in the rendered DOM.
 */
export function generateButtonEffectJS(state, options = {}) {
  const effectJS = getButtonEffectJS(state, resolveButtonSelector(state, options)) || '';
  return [effectJS, BUBBLY_BUTTON_JS].filter(Boolean).join('\n\n');
}

/**
 * Generate local.css: per-instance section CSS with unique #exportId selectors.
 * Rewrites [data-pb-section="sectionId"] → #exportId.
 */
/**
 * The sections whose CSS is genuinely above the fold for ONE route (IF-7).
 *
 * Two corrections to what this used to return, both of which made critical.css
 * describe a page nobody visits:
 *
 *   - **The footer is not above the fold.** It was included from the start,
 *     against the function's own docstring, so every page paid to inline the
 *     styles for the thing furthest from the viewport.
 *   - **Critical CSS is per ROUTE.** The old set unioned the first section of
 *     EVERY page, so the home page blocked on the hero styles of the services
 *     page and vice versa. With N pages that is N-1 heroes of dead weight in a
 *     render-blocking position.
 *
 * `pageId` selects the route. Omitting it keeps the union across pages, which
 * is what a single shared critical.css needs and what the pre-IF-7 callers
 * expect — the union is a superset, so an unconverted caller renders correctly
 * and merely carries the old cost.
 */
export function collectCriticalSectionIds(state, pageId = null) {
  const ids = new Set();
  if (state.sharedSections.navigation) {
    ids.add(state.sharedSections.navigation.sectionId);
  }
  const pages = pageId == null ? state.pages : state.pages.filter((p) => p.id === pageId);
  for (const page of pages) {
    if (page.sections.length > 0) {
      ids.add(page.sections[0].sectionId);
    }
  }
  return ids;
}

/**
 * Serialize CSS for an inline `<style>` element.
 *
 * An HTML parser ends a `<style>` at the first literal `</style`, case
 * insensitively and regardless of CSS syntax — so a template whose authored CSS
 * contains that sequence inside a string or a comment terminates the block
 * early and everything after it becomes document text, with whatever follows
 * `<script>` running. Escaping the slash keeps the CSS byte-equivalent to the
 * parser (`<\/style` is the same token stream to a CSS tokenizer) while making
 * the sequence invisible to the HTML one.
 */
export function escapeStyleElementCSS(css) {
  return String(css == null ? '' : css).replace(/<\/(style|script)/gi, '<\\/$1');
}

/**
 * The one deferred-stylesheet emitter, shared by both exporters (IF-7).
 *
 * `rel=preload as=style` fetches without blocking render, and the onload swap
 * turns it into a real stylesheet. The `<noscript>` copy is not decoration: the
 * onload swap is the only thing that applies these sheets, so without it a
 * visitor with JS disabled gets the inline critical CSS and NOTHING else — a
 * page that looks broken rather than unstyled.
 *
 * The flat exporter calls this whole function. The 11ty exporter cannot — its
 * head wraps each sheet in a per-route `{% if %}` and needs ONE combined
 * <noscript> at the end — so it calls `preloadStylesheetLink` per href instead.
 * That single helper is what the two share; the noscript structure is not.
 *
 * @param {string[]} hrefs
 * @returns {string} link + noscript markup, or '' for an empty list
 */
/**
 * The one place the deferred-stylesheet <link> shape exists. Both exporters
 * emit it through here so a change to the swap cannot land on one surface only.
 *
 * The handler body is a CONSTANT — nothing is interpolated into JS-string
 * position, which is the injection the sweep's rule is actually about. The href
 * goes into an HTML attribute and is escaped as one. What the inline handler
 * does cost is real and accepted: the exported CSP keeps 'unsafe-inline' in
 * script-src for it (golden B3b). The alternative — swapping rel from an
 * external script — cannot run before that script loads, which is precisely the
 * blocking fetch this pattern exists to avoid.
 */
/**
 * The swap handler, as a CONSTANT, so every target shares one definition.
 *
 * The Astro layout cannot call `preloadStylesheetLink()`: its href carries a
 * build-time cache-bust (`?v=${BUILD.hash}`) and must therefore be an Astro
 * expression attribute, not a pre-escaped string. Without this export it would
 * hardcode its own copy of the handler — which is exactly the drift
 * builder/CLAUDE.md records for the flat path ("this rule previously claimed
 * both exporters called deferredStylesheetTags(). They did not."). Sharing the
 * handler keeps the one part that must not diverge in one place.
 */
export const PRELOAD_STYLESHEET_ONLOAD = "this.onload=null;this.rel='stylesheet'";

export function preloadStylesheetLink(href) {
  // Constant handler body; nothing reaches JS-string position. See the docblock.
  // pb-sweep-exempt: export-no-inline-event-handler — no non-inline form exists
  return `<link rel="preload" as="style" href="${escapeHTML(href)}" onload="${PRELOAD_STYLESHEET_ONLOAD}">`;
}

export function deferredStylesheetTags(hrefs) {
  const list = (Array.isArray(hrefs) ? hrefs : []).filter(Boolean);
  if (list.length === 0) return '';
  const preloads = list.map(preloadStylesheetLink).join('\n  ');
  const fallback = list
    .map((href) => `<link rel="stylesheet" href="${escapeHTML(href)}">`)
    .join('\n    ');
  return `${preloads}\n  <noscript>\n    ${fallback}\n  </noscript>`;
}

/**
 * Generate critical.css: above-the-fold styles for navigation + hero sections.
 * Extracts CSS for the nav and first section of each page.
 */
/**
 * Extract complete @media blocks from CSS that reference a given sectionId.
 * Uses brace-depth tracking instead of regex to handle nested selectors,
 * @keyframes, and comments containing braces.
 */
export function extractMediaBlocksForId(css, sectionId) {
  const results = [];
  const attrSelector = `[data-pb-section="${sectionId}"]`;
  const idSelector = `#${sectionId}`;
  let i = 0;
  const len = css.length;

  while (i < len) {
    // Skip block comments
    if (css[i] === '/' && css[i + 1] === '*') {
      const end = css.indexOf('*/', i + 2);
      i = end === -1 ? len : end + 2;
      continue;
    }

    // Detect @media (but not @keyframes etc at top level — we want @media only)
    if (css[i] === '@' && css.substring(i, i + 6) === '@media') {
      const blockStart = i;
      // Find the opening brace
      let j = i + 6;
      while (j < len && css[j] !== '{') j++;
      if (j >= len) break;

      // Track brace depth to find the complete block
      let depth = 0;
      let k = j;
      while (k < len) {
        const ch = css[k];
        // Skip comments inside the block
        if (ch === '/' && css[k + 1] === '*') {
          const end = css.indexOf('*/', k + 2);
          k = end === -1 ? len : end + 2;
          continue;
        }
        // Skip string literals (handles `content: "}"`)
        if (ch === '"' || ch === "'") {
          const quote = ch;
          k++;
          while (k < len && css[k] !== quote) {
            if (css[k] === '\\') k++; // skip escaped char
            k++;
          }
          k++;
          continue;
        }
        if (ch === '{') depth++;
        else if (ch === '}') {
          depth--;
          if (depth === 0) {
            const block = css.substring(blockStart, k + 1);
            // Check if block references the sectionId
            if (block.includes(attrSelector) || block.includes(idSelector)) {
              results.push(block);
            }
            i = k + 1;
            break;
          }
        }
        k++;
      }
      if (depth !== 0) break; // malformed CSS, bail
      continue;
    }

    i++;
  }

  return results;
}

function sectionIdsInSelector(selectorNode, allSectionIds) {
  const found = new Set();
  csstree.walk(selectorNode, (node) => {
    if (node.type === 'IdSelector' && allSectionIds.has(node.name)) found.add(node.name);
    if (node.type !== 'AttributeSelector') return;
    const name = node.name?.name || node.name;
    const value = node.value?.value || node.value?.name || null;
    if (name === 'data-pb-section' && allSectionIds.has(value)) found.add(value);
  });
  return found;
}

/**
 * Route-aware template CSS closure for first paint.
 *
 * Keep rules owned by this route's nav/hero, keep genuinely common rules, and
 * recursively prune conditional blocks. Definition at-rules and declarations
 * remain available so keyframes, @font-face, @property, and custom-property
 * inputs cannot be deferred behind a rule that consumes them. If parsing fails,
 * return the complete stylesheet: critical extraction may cost bytes, but it
 * must never make the first paint incorrect.
 */
export function extractCriticalTemplateCSS(css, criticalIds, allSectionIds) {
  const source = String(css || '');
  if (!source.trim()) return '';
  const critical = criticalIds instanceof Set ? criticalIds : new Set(criticalIds || []);
  const all = allSectionIds instanceof Set ? allSectionIds : new Set(allSectionIds || []);
  let ast;
  try {
    ast = csstree.parse(source, { positions: false });
  } catch {
    return source;
  }

  // Generating declaration nodes one at a time drops their list separators:
  // css-tree emits `font-family:Brand` for a Declaration and relies on the
  // surrounding List serializer to insert `;`. Definition at-rules are
  // indivisible dependencies, so serialize their complete AST node instead of
  // recursively joining declarations into invalid CSS.
  const definitionAtRules = new Set([
    'font-face',
    'property',
    'counter-style',
    'font-feature-values',
    'font-palette-values'
  ]);

  const filterNode = (node) => {
    if (node.type === 'Rule') {
      const selectors = node.prelude?.children?.toArray?.() || [];
      if (selectors.length === 0) return csstree.generate(node);
      const kept = selectors.filter((selector) => {
        const mentioned = sectionIdsInSelector(selector, all);
        if (mentioned.size === 0) return true;
        return [...mentioned].some((id) => critical.has(id));
      });
      if (kept.length === 0) return '';
      return `${kept.map((selector) => csstree.generate(selector)).join(',')}{${csstree
        .generate(node.block)
        .slice(1, -1)}}`;
    }
    if (node.type === 'Atrule') {
      if (definitionAtRules.has(String(node.name).toLowerCase())) return csstree.generate(node);
      const prelude = node.prelude ? ` ${csstree.generate(node.prelude)}` : '';
      if (!node.block) return `@${node.name}${prelude};`;
      const children = node.block.children?.toArray?.() || [];
      const body = children.map(filterNode).filter(Boolean).join('');
      if (!body) return '';
      return `@${node.name}${prelude}{${body}}`;
    }
    // Declarations inside definition at-rules are dependencies, not selectors.
    return csstree.generate(node);
  };

  return (ast.children?.toArray?.() || []).map(filterNode).filter(Boolean).join('');
}

function minifyCriticalCSS(css) {
  const source = String(css || '');
  if (!source.trim()) return '';
  try {
    return csstree.generate(csstree.parse(source, { positions: false }));
  } catch {
    return source;
  }
}

function criticalHtmlInventory(html) {
  const inventory = {
    tags: new Set(['html', 'body', 'main']),
    classes: new Set(['dark-mode']),
    ids: new Set(),
    attrs: new Set(['class', 'id'])
  };
  const source = String(html || '');
  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(
        `<!doctype html><html><body><main>${source}</main></body></html>`,
        'text/html'
      );
      for (const el of doc.querySelectorAll('*')) {
        inventory.tags.add(el.tagName.toLowerCase());
        if (el.id) inventory.ids.add(el.id);
        for (const name of el.classList || []) inventory.classes.add(name);
        for (const attr of el.attributes || []) inventory.attrs.add(attr.name.toLowerCase());
      }
      return inventory;
    } catch {
      /* fall through to the dependency-only regex inventory */
    }
  }
  for (const match of source.matchAll(/<([a-z][\w-]*)\b/gi)) {
    inventory.tags.add(match[1].toLowerCase());
  }
  for (const match of source.matchAll(/\bclass\s*=\s*["']([^"']*)["']/gi)) {
    for (const name of match[1].split(/\s+/)) if (name) inventory.classes.add(name);
  }
  for (const match of source.matchAll(/\bid\s*=\s*["']([^"']+)["']/gi)) {
    inventory.ids.add(match[1]);
  }
  return inventory;
}

function selectorCanAffectInventory(selector, inventory) {
  // Tokens inside :not() are negative dependencies. Remove simple negations
  // before checking positive tag/class/id requirements; if parsing a complex
  // nested form fails, keep it conservatively.
  const selectorText = csstree.generate(selector);
  if (selectorText.includes(':not(')) {
    const positiveText = selectorText.replace(/:not\((?:[^()]|\([^()]*\))*\)/g, '');
    if (!positiveText.trim()) return true;
    try {
      return selectorCanAffectInventory(
        csstree.parse(positiveText, { context: 'selector', positions: false }),
        inventory
      );
    } catch {
      return true;
    }
  }
  let relevant = true;
  csstree.walk(selector, (node) => {
    if (!relevant) return;
    if (node.type === 'ClassSelector' && !inventory.classes.has(node.name)) relevant = false;
    if (node.type === 'IdSelector' && !inventory.ids.has(node.name)) relevant = false;
    if (
      node.type === 'TypeSelector' &&
      node.name !== '*' &&
      !inventory.tags.has(String(node.name).toLowerCase())
    ) {
      relevant = false;
    }
    if (node.type === 'AttributeSelector') {
      const name = String(node.name?.name || node.name || '').toLowerCase();
      if (name && !inventory.attrs.has(name)) relevant = false;
    }
  });
  return relevant;
}

/**
 * The root/common half of the first-paint closure. The deferred root sheet can
 * keep editor/runtime utilities that no element above the fold can consume;
 * the inline copy retains only selectors whose tag/class/id/attribute
 * dependencies exist in the route's nav + hero. Definition at-rules remain
 * whole so a retained rule never loses a font, property, or animation.
 */
export function extractCriticalCommonCSS(css, criticalHtml) {
  const source = String(css || '');
  if (!source.trim()) return '';
  let ast;
  try {
    ast = csstree.parse(source, { positions: false });
  } catch {
    return source;
  }
  const inventory = criticalHtmlInventory(criticalHtml);
  const definitionAtRules = new Set([
    'font-face',
    'keyframes',
    '-webkit-keyframes',
    'property',
    'counter-style'
  ]);
  const filterNode = (node) => {
    if (node.type === 'Rule') {
      const selectors = node.prelude?.children?.toArray?.() || [];
      const kept = selectors.filter((selector) => selectorCanAffectInventory(selector, inventory));
      if (kept.length === 0) return '';
      return `${kept.map((selector) => csstree.generate(selector)).join(',')}{${csstree
        .generate(node.block)
        .slice(1, -1)}}`;
    }
    if (node.type === 'Atrule') {
      if (definitionAtRules.has(String(node.name).toLowerCase())) return csstree.generate(node);
      const prelude = node.prelude ? ` ${csstree.generate(node.prelude)}` : '';
      if (!node.block) return `@${node.name}${prelude};`;
      const body = (node.block.children?.toArray?.() || [])
        .map(filterNode)
        .filter(Boolean)
        .join('');
      return body ? `@${node.name}${prelude}{${body}}` : '';
    }
    return csstree.generate(node);
  };
  return (ast.children?.toArray?.() || []).map(filterNode).filter(Boolean).join('');
}

export function generateCriticalCSS(state, exportIdMap, resolvedMap = null, options = {}) {
  const stripAstroCid = options.stripAstroCid === true;
  const rawTemplateCSS = typeof options.templateCSS === 'string' ? options.templateCSS : '';
  const componentRuleStripOptions = resolveComponentStripPlan(state, rawTemplateCSS);
  // IF-7: `pageId` narrows this to ONE route. Omitted, it keeps the pre-IF-7
  // union across pages — a superset, so an unconverted caller still renders.
  const pageId = options.pageId ?? null;
  const criticalIds = collectCriticalSectionIds(state, pageId);
  const blocks = [];
  const templateExportIds = buildTemplateExportIdMap(state, exportIdMap);
  const preparedTemplateCSS = scopeTemplateSupportBlocks(rawTemplateCSS);
  const templateCSS = stripAstroCid
    ? stripAstroCidSelectors(preparedTemplateCSS)
    : preparedTemplateCSS;

  const seen = new Set();
  const criticalInstances = [];
  if (state.sharedSections.navigation) criticalInstances.push(state.sharedSections.navigation);
  // The footer is deliberately NOT here (IF-7). It is the element furthest from
  // the viewport, and inlining it made every page pay render-blocking bytes for
  // the last thing anyone sees.
  const criticalPages = pageId == null ? state.pages : state.pages.filter((p) => p.id === pageId);
  for (const page of criticalPages) {
    if (page.sections.length > 0) criticalInstances.push(page.sections[0]);
  }

  // Common/theme/font CSS is a first-paint dependency just as much as the
  // route's nav and hero. root.css is still emitted as the deferred canonical
  // sheet; this inline copy closes the time before its onload swap.
  if (typeof options.rootCSS === 'string' && options.rootCSS.trim()) {
    blocks.push(
      extractCriticalCommonCSS(
        options.rootCSS,
        criticalInstances.map((section) => section.html || '').join('\n')
      )
    );
  }

  // Extract the route's complete template closure (base + conditional rules),
  // not merely @media blocks that happen to mention a critical section.
  if (templateCSS) {
    let criticalTemplateCSS = extractCriticalTemplateCSS(
      templateCSS,
      criticalIds,
      new Set(templateExportIds.keys())
    );
    for (const id of criticalIds) {
      criticalTemplateCSS = rewriteTemplateSelectorsForSection(
        criticalTemplateCSS,
        id,
        templateExportIds
      );
    }
    criticalTemplateCSS = rewriteResolvedAssetUrls(criticalTemplateCSS, resolvedMap);
    if (criticalTemplateCSS) blocks.push(criticalTemplateCSS);
  }

  // Extract from per-section CSS (catalog sections)
  for (const section of criticalInstances) {
    if (!section.css) continue;
    const exportId = exportIdMap.get(section.instanceId);
    if (!exportId || seen.has(exportId)) continue;
    seen.add(exportId);

    // Absolutize url(/...) against the section's source template domain so
    // the URLs in CSS match the keys in resolvedMap (which was built from the
    // same absolutization in asset-resolver.collectImageUrls).
    let rewritten = section.templateSiteName
      ? rewriteCssAssetUrlsPreferVendored(
          section.css,
          section.templateSiteName,
          getVendoredAssetMapSync(section.templateSiteName)
        )
      : section.css;
    rewritten = prepareSectionCSSForExport(section, rewritten, componentRuleStripOptions);
    if (!section.preserveTypography) rewritten = normalizeSectionTypography(rewritten);
    if (stripAstroCid) rewritten = stripAstroCidSelectors(rewritten);
    rewritten = rewriteTemplateSelectorsForSection(rewritten, section.sectionId, templateExportIds);
    blocks.push(`/* ${exportId} */\n${rewriteResolvedAssetUrls(rewritten, resolvedMap)}`);
  }

  // Section + element overrides for critical sections — keeps critical.css
  // consistent with local.css / page-bundle.css (which already include these
  // via generateLocalCSS / generatePageBundles). Both files are render-blocking
  // so cascade resolves correctly either way; this is correctness, not CLS.
  // The same blocks also live in local.css / <slug>.css by design — the
  // duplication is acceptable cascade overhead.
  for (const section of criticalInstances) {
    const exportId = exportIdMap.get(section.instanceId);
    if (!exportId) continue;
    for (const block of buildSectionOverrideBlocks(section, exportId)) {
      blocks.push(rewriteResolvedAssetUrls(block, resolvedMap));
    }
  }

  return minifyCriticalCSS(blocks.join('\n\n'));
}

export function generateLocalCSS(state, exportIdMap, resolvedMap = null, options = {}) {
  const stripAstroCid = options.stripAstroCid === true;
  const rawTemplateCSS = typeof options.templateCSS === 'string' ? options.templateCSS : '';
  const componentRuleStripOptions = resolveComponentStripPlan(state, rawTemplateCSS);
  const templateSectionIds =
    options.templateSectionIds instanceof Set ? options.templateSectionIds : null;
  const blocks = [];
  const templateExportIds = buildTemplateExportIdMap(state, exportIdMap);

  // Template CSS — global styles from template websites (stored in _styles.css).
  // Rewrite [data-pb-section="sectionId"] → #sectionId to match exported id attributes.
  if (rawTemplateCSS) {
    const preparedTemplateCSS = scopeTemplateSupportBlocks(rawTemplateCSS);
    let rewritten = stripAstroCid
      ? stripAstroCidSelectors(preparedTemplateCSS)
      : preparedTemplateCSS;
    if (templateSectionIds) {
      rewritten = filterTemplateCssForSectionIds(rewritten, templateSectionIds);
    }
    for (const sectionId of templateExportIds.keys()) {
      rewritten = rewriteTemplateSelectorsForSection(rewritten, sectionId, templateExportIds);
    }
    if (rewritten) {
      blocks.push(`/* Template styles */\n${rewriteResolvedAssetUrls(rewritten, resolvedMap)}`);
    }
  }

  const seen = new Map();
  const allInstances = collectAllSectionInstances(state);

  for (const section of allInstances) {
    if (!section.css) continue;
    const exportId = exportIdMap.get(section.instanceId);
    if (!exportId) continue;

    const key = sectionKey(section);
    if (!seen.has(key)) seen.set(key, new Set());
    const seenIds = seen.get(key);

    if (seenIds.has(exportId)) continue;
    seenIds.add(exportId);

    let rewritten = section.templateSiteName
      ? rewriteCssAssetUrlsPreferVendored(
          section.css,
          section.templateSiteName,
          getVendoredAssetMapSync(section.templateSiteName)
        )
      : section.css;
    rewritten = prepareSectionCSSForExport(section, rewritten, componentRuleStripOptions);
    if (!section.preserveTypography) rewritten = normalizeSectionTypography(rewritten);
    if (stripAstroCid) rewritten = stripAstroCidSelectors(rewritten);
    rewritten = rewriteTemplateSelectorsForSection(rewritten, section.sectionId, templateExportIds);
    blocks.push(`/* ${exportId} */\n${rewriteResolvedAssetUrls(rewritten, resolvedMap)}`);
  }

  const templateFidelityCSS = assembleTemplateFidelityCSS(allInstances, {
    selectorForSection(section) {
      const exportId = exportIdMap.get(section.instanceId);
      return exportId ? `#${exportId}` : '';
    }
  });
  if (templateFidelityCSS) {
    blocks.push(
      `/* Template fidelity fixes */\n${rewriteResolvedAssetUrls(templateFidelityCSS, resolvedMap)}`
    );
  }

  const motionStaticCSS = assembleMotionStaticCSS(allInstances);
  if (motionStaticCSS) {
    blocks.push(`/* Static carousel collapse */\n${motionStaticCSS}`);
  }

  // Section-level overrides, including shared navigation/footer.
  for (const section of allInstances) {
    const exportId = exportIdMap.get(section.instanceId);
    if (!exportId) continue;
    blocks.push(...buildSectionOverrideBlocks(section, exportId));
  }

  return blocks.join('\n\n');
}

// Repaired sections must retain the canonical canvas cascade. Inlining base
// !important declarations would defeat responsive rules and lose picture-parent
// targeting. Untouched sections retain the established export byte shape.
export function hasRepairedElementCSS(section) {
  return (
    section.editableIdRepairs?.version === 1 &&
    Array.isArray(section.editableIdRepairs.records) &&
    section.editableIdRepairs.records.length > 0
  );
}

// Canvas selectors are scoped by a wrapper; the export id lives on the
// section root itself. Preserve both descendant matches and the former wrapper's
// direct child, including SVG/picture roots and parent-targeted selectors.
function exportRepairedElementCSS(css, exportId) {
  const ast = csstree.parse(css);
  csstree.walk(ast, {
    visit: 'Rule',
    enter(rule) {
      if (rule.prelude?.type !== 'SelectorList') return;
      const selectors = [];
      for (const selector of rule.prelude.children) {
        const items = selector.children.toArray();
        if (
          items[0]?.type !== 'AttributeSelector' ||
          items[0].name.name !== 'data-pb-instance' ||
          items[1]?.type !== 'Combinator'
        ) {
          selectors.push(selector);
          continue;
        }
        const id = { type: 'IdSelector', loc: null, name: exportId };
        const rootItems = items.slice(2).map((item) => csstree.clone(item));
        rootItems.splice(rootItems[0]?.type === 'TypeSelector' ? 1 : 0, 0, id);
        selectors.push({ ...selector, children: new csstree.List().fromArray(rootItems) });
        if (items[1].name !== '>') {
          selectors.push({
            ...selector,
            children: new csstree.List().fromArray([id, ...items.slice(1)])
          });
        }
      }
      rule.prelude.children = new csstree.List().fromArray(selectors);
    }
  });
  csstree.walk(ast, {
    visit: 'AttributeSelector',
    enter(node) {
      if (node.name.name === 'data-pb-eid') node.name.name = 'data-style-id';
    }
  });
  return csstree.generate(ast);
}

function buildSectionOverrideBlocks(section, exportId) {
  if (!section?.overrides || !exportId) return [];

  const blocks = [];
  const {
    backgroundColor,
    paddingTop,
    paddingBottom,
    marginTop,
    marginBottom,
    customCSS,
    columnCount
  } = section.overrides;
  const sImp = () => ' !important';
  const rules = [];
  if (backgroundColor)
    rules.push(`background-color: ${backgroundColor}${sImp('backgroundColor')};`);
  if (paddingTop) rules.push(`padding-top: ${paddingTop}${sImp('paddingTop')};`);
  if (paddingBottom) rules.push(`padding-bottom: ${paddingBottom}${sImp('paddingBottom')};`);
  if (marginTop) rules.push(`margin-top: ${marginTop}${sImp('marginTop')};`);
  if (marginBottom) rules.push(`margin-bottom: ${marginBottom}${sImp('marginBottom')};`);
  if (rules.length > 0) {
    blocks.push(`#${exportId} { ${rules.join(' ')} }`);
  }
  if (customCSS) {
    const scoped = scopeCSS(customCSS, `#${exportId}`);
    if (scoped) blocks.push(scoped);
  }

  // Decorative bleed — same builder as the preview, scoped by export id. In an
  // export `#exportId` IS the section root (there is no wrapper), which is why
  // the builder emits its own stacking context rather than relying on the
  // canvas-only `.pb-section-wrapper` z-index ladder.
  const bleedCSS = buildDecorativeBleedCSS(`#${exportId}`, section.overrides.decorativeBleed);
  if (bleedCSS) blocks.push(bleedCSS);

  // Column-count override — mirrors css-assembler.js but scopes by the
  // export id (`#exportId`) instead of `[data-pb-instance]` because
  // exported HTML strips builder attrs and assigns each section a stable id.
  const colN = _normalizeColumnCount(columnCount);
  if (colN) {
    const group = detectMultiItemGroups(section);
    if (group) {
      blocks.push(_buildColumnCountCSSForExport(`#${exportId}`, group.groupSelector, colN));
    }
  }

  const repairedElementCSS = hasRepairedElementCSS(section);
  if (repairedElementCSS) {
    const elementCSS = assembleSectionOverrideCSS({
      ...section,
      instanceId: exportId,
      overrides: {
        elements: section.overrides.elements,
        responsive: Object.fromEntries(
          Object.entries(section.overrides.responsive || {}).map(([viewport, bucket]) => [
            viewport,
            { elements: bucket?.elements }
          ])
        )
      }
    });
    if (elementCSS) blocks.push(exportRepairedElementCSS(elementCSS, exportId));
  }

  const responsiveQueries = {
    mobile: '(max-width: 47.999rem)',
    tablet: '(min-width: 48rem) and (max-width: 63.999rem)',
    desktop: '(min-width: 64rem)'
  };
  for (const viewport of ['desktop', 'tablet', 'mobile']) {
    const bucket = section.overrides.responsive?.[viewport];
    if (!bucket || typeof bucket !== 'object') continue;
    const responsiveRules = [];
    for (const field of [
      'backgroundColor',
      'paddingTop',
      'paddingBottom',
      'marginTop',
      'marginBottom'
    ]) {
      if (!bucket[field]) continue;
      responsiveRules.push(`${camelToKebab(field)}: ${bucket[field]} !important;`);
    }
    const innerBlocks = [];
    if (responsiveRules.length) innerBlocks.push(`#${exportId} { ${responsiveRules.join(' ')} }`);
    if (bucket.elements && !repairedElementCSS) {
      const elementCSS = assembleSectionOverrideCSS({
        ...section,
        instanceId: exportId,
        overrides: { elements: bucket.elements }
      })
        .replaceAll(`[data-pb-instance="${exportId}"]`, `#${exportId}`)
        .replace(/\[data-pb-eid="([^"]+)"\]/g, '[data-style-id="$1"]');
      if (elementCSS) innerBlocks.push(elementCSS);
    }
    if (innerBlocks.length) {
      const indented = innerBlocks
        .join('\n')
        .split('\n')
        .map((line) => `  ${line}`)
        .join('\n');
      blocks.push(`@media ${responsiveQueries[viewport]} {\n${indented}\n}`);
    }
  }
  return blocks;
}

function _normalizeColumnCount(value) {
  if (value == null || value === '') return null;
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

function _buildColumnCountCSSForExport(scope, groupSelector, n) {
  return [
    '@media (min-width: 64rem) {',
    `  ${scope} ${groupSelector} {`,
    `    grid-template-columns: repeat(${n}, 1fr) !important;`,
    '    display: grid !important;',
    '  }',
    `  ${scope} ${groupSelector} > .cs-item {`,
    '    width: auto !important;',
    '    grid-column: auto !important;',
    '    flex-basis: auto !important;',
    '  }',
    '}'
  ].join('\n');
}

function buildFallbackRoutingTable(state) {
  const routingTable = [];
  const existingSlugs = new Set();
  for (let i = 0; i < (state?.pages || []).length; i++) {
    const page = state.pages[i];
    if (i === 0) {
      routingTable.push({ id: page.id, slug: '', permalink: '/', emit: true });
      continue;
    }
    const slug = generateSlug(page?.slug || page?.name || `page-${i + 1}`, existingSlugs);
    routingTable.push({ id: page.id, slug, permalink: `/${slug}/`, emit: true });
  }
  return routingTable;
}

function selectorReferencesSectionIds(selector, sectionIds) {
  let matched = false;

  csstree.walk(selector, {
    visit: 'IdSelector',
    enter(node) {
      if (sectionIds.has(node.name)) matched = true;
    }
  });
  if (matched) return true;

  csstree.walk(selector, {
    visit: 'AttributeSelector',
    enter(node) {
      const name = selectorAttrName(node);
      const value = node?.value?.value || '';
      if (name === 'data-pb-section' && sectionIds.has(value)) {
        matched = true;
      }
    }
  });

  return matched;
}

function filterTemplateCssForSectionIds(templateCSS, sectionIds) {
  if (!templateCSS || !(sectionIds instanceof Set) || sectionIds.size === 0) return '';

  const { css: cssWithoutSupportBlocks, supportBlocks } = splitTemplateSupportBlocks(templateCSS);
  let ast;
  try {
    ast = csstree.parse(cssWithoutSupportBlocks);
  } catch (err) {
    console.warn('[export] filterTemplateCssForSectionIds parse failed:', err.message);
    return supportBlocks.join('\n\n');
  }

  const keepWholeAtrule = new Set(['font-face', 'keyframes', '-webkit-keyframes']);

  const pruneBlock = (block) => {
    if (!block?.children?.forEach) return;

    block.children.forEach((child, item) => {
      if (child.type === 'Rule' && child.prelude?.type === 'SelectorList') {
        child.prelude.children.forEach((selector, selectorItem) => {
          if (!selectorReferencesSectionIds(selector, sectionIds)) {
            child.prelude.children.remove(selectorItem);
          }
        });

        if (child.prelude.children.isEmpty) {
          block.children.remove(item);
        }
        return;
      }

      if (child.type === 'Atrule' && child.block) {
        const atName = String(child.name || '').toLowerCase();
        if (keepWholeAtrule.has(atName)) return;
        pruneBlock(child.block);
        if (child.block.children?.isEmpty) {
          block.children.remove(item);
        }
      }
    });
  };

  pruneBlock(ast);

  try {
    const filtered = csstree.generate(ast);
    return [filtered, ...supportBlocks].filter(Boolean).join('\n\n');
  } catch (err) {
    console.warn('[export] filterTemplateCssForSectionIds generate failed:', err.message);
    return supportBlocks.join('\n\n');
  }
}

function splitTemplateSupportBlocks(templateCSS) {
  const css = String(templateCSS || '');
  const supportBlocks = [];
  let out = '';
  let cursor = 0;

  while (cursor < css.length) {
    const start = css.indexOf(TEMPLATE_SUPPORT_MARKER_PREFIX, cursor);
    if (start === -1) {
      out += css.slice(cursor);
      break;
    }

    const startEnd = css.indexOf('*/', start);
    if (startEnd === -1) {
      out += css.slice(cursor);
      break;
    }

    const siteName = css.slice(start + TEMPLATE_SUPPORT_MARKER_PREFIX.length, startEnd).trim();
    const endMarker = `/* /pb-template-support:${siteName} */`;
    const end = css.indexOf(endMarker, startEnd + 2);
    if (!siteName || end === -1) {
      out += css.slice(cursor);
      break;
    }

    out += css.slice(cursor, start);
    supportBlocks.push(css.slice(start, end + endMarker.length));
    cursor = end + endMarker.length;
  }

  return { css: out, supportBlocks };
}

/**
 * Build per-route CSS bundles for non-home 11ty pages.
 * Each bundle is self-contained for the sections on that route.
 *
 * @returns {Map<string, string>} route.slug -> css
 */
export function generatePageBundles(
  state,
  exportIdMap,
  resolvedMap = null,
  features = null,
  options = {}
) {
  void features;
  const stripAstroCid = options.stripAstroCid === true;
  const rawTemplateCSS = typeof options.templateCSS === 'string' ? options.templateCSS : '';
  const componentRuleStripOptions = resolveComponentStripPlan(state, rawTemplateCSS);
  const fallbackRoutingTable = buildFallbackRoutingTable(state);
  const routingTable = Array.isArray(options.routingTable)
    ? options.routingTable
    : fallbackRoutingTable;
  const bundles = new Map();
  const templateExportIds = buildTemplateExportIdMap(state, exportIdMap);
  const emittedRoutes = [];
  const emittedByPageIndex = new Map();

  for (let i = 0; i < state.pages.length; i++) {
    const page = state.pages[i];
    const route = routingTable[i] || fallbackRoutingTable[i];
    if (!route?.emit) continue;
    const meta = { pageIndex: i, page, route, isFirstPage: i === 0 };
    emittedRoutes.push(meta);
    emittedByPageIndex.set(i, meta);
  }

  const totalEmitted = emittedRoutes.length;
  if (totalEmitted === 0) return bundles;

  const keyToPages = new Map();
  for (const meta of emittedRoutes) {
    for (const section of meta.page.sections) {
      const key = sectionKey(section);
      if (!key) continue;
      if (!keyToPages.has(key)) keyToPages.set(key, new Set());
      keyToPages.get(key).add(meta.pageIndex);
    }
  }

  const includedKeysByPage = new Map();
  for (const [key, pages] of keyToPages) {
    // Global page-section styles are expected in root.css / critical.css.
    if (pages.size === totalEmitted) continue;

    for (const pageIndex of pages) {
      const meta = emittedByPageIndex.get(pageIndex);
      if (!meta || meta.isFirstPage) continue;
      if (!includedKeysByPage.has(pageIndex)) includedKeysByPage.set(pageIndex, new Set());
      includedKeysByPage.get(pageIndex).add(key);
    }
  }

  const preparedTemplateCSS = scopeTemplateSupportBlocks(rawTemplateCSS);
  const templateCSSSource = stripAstroCid
    ? stripAstroCidSelectors(preparedTemplateCSS)
    : preparedTemplateCSS;

  for (const meta of emittedRoutes) {
    if (meta.isFirstPage) continue; // home-only styles stay in local.css
    const includeKeys = includedKeysByPage.get(meta.pageIndex);
    if (!includeKeys || includeKeys.size === 0) continue;

    const blocks = [];
    const templateSectionIds = new Set();
    const seenExportIds = new Set();

    for (const section of meta.page.sections) {
      const key = sectionKey(section);
      if (!includeKeys.has(key)) continue;

      if (section.sectionId) templateSectionIds.add(section.sectionId);

      const exportId = exportIdMap.get(section.instanceId);
      if (!exportId || seenExportIds.has(exportId)) continue;
      seenExportIds.add(exportId);

      if (section.css) {
        let rewritten = section.templateSiteName
          ? rewriteCssAssetUrlsPreferVendored(
              section.css,
              section.templateSiteName,
              getVendoredAssetMapSync(section.templateSiteName)
            )
          : section.css;
        rewritten = prepareSectionCSSForExport(section, rewritten, componentRuleStripOptions);
        if (!section.preserveTypography) rewritten = normalizeSectionTypography(rewritten);
        if (stripAstroCid) rewritten = stripAstroCidSelectors(rewritten);
        rewritten = rewriteTemplateSelectorsForSection(
          rewritten,
          section.sectionId,
          templateExportIds
        );
        blocks.push(`/* ${exportId} */\n${rewriteResolvedAssetUrls(rewritten, resolvedMap)}`);
      }

      blocks.push(...buildSectionOverrideBlocks(section, exportId));
    }

    if (templateCSSSource && templateSectionIds.size > 0) {
      let templateBlock = filterTemplateCssForSectionIds(templateCSSSource, templateSectionIds);
      if (templateBlock) {
        for (const sectionId of templateSectionIds) {
          templateBlock = rewriteTemplateSelectorsForSection(
            templateBlock,
            sectionId,
            templateExportIds
          );
        }
        blocks.unshift(
          `/* Template styles */\n${rewriteResolvedAssetUrls(templateBlock, resolvedMap)}`
        );
      }
    }

    const css = blocks.join('\n\n').trim();
    if (!css) continue;
    if (meta.route?.slug) bundles.set(meta.route.slug, css);
  }

  return bundles;
}

/**
 * Generate section JS for export. Each instance gets its own IIFE
 * scoped to its unique #exportId via Proxy-based document facade.
 */
export function generateExportScripts(state, exportIdMap, options = {}) {
  const descendantRenames =
    options.descendantRenames instanceof Map ? options.descendantRenames : null;
  // Three of the four callers write an external `sections.js`; the saved-preview
  // bundler writes an inline `<script>` and says so. See buildFacadeIIFE's
  // `embedding` for why this is not the same question as `mode`.
  const embedding = options.embedding === 'inline-html' ? 'inline-html' : 'external-js';
  const scripts = [];
  const allInstances = collectAllSectionInstances(state);

  const emitted = new Set();

  for (const section of allInstances) {
    // AG-4: "neither js nor stamp". A behaviour-stamped section can carry EMPTY
    // js — its behaviour lives in data/behaviours/ and is attached from the
    // stamp — so the old `!section.js` skip dropped exactly the sections this
    // release exists to make work.
    const stamps = behaviourStampsInHtml(section.html).filter((t) =>
      behaviourIsShipped(t.id, t.variant)
    );
    const hasJs = !!(section.js && section.js.trim());
    if (!hasJs && stamps.length === 0) continue;
    const exportId = exportIdMap.get(section.instanceId);
    if (!exportId || emitted.has(exportId)) continue;

    // A strict guest parent cannot compile strings. Its document gives every
    // facade a native script unit so a syntax error cannot poison later ones.
    if (hasJs && !options.nativeScriptUnits) {
      try {
        new Function(section.js);
      } catch (e) {
        console.warn('[export] Skipping malformed JS for', section.sectionId, e.message);
        continue;
      }
    }
    emitted.add(exportId);

    // The bind call runs AFTER the section's own script, inside the same facade,
    // so it inherits the scoped document proxy — see __pbBindDoc.
    const bindJS = stamps.length ? behaviourBindJS(section.html) : '';

    scripts.push(
      buildFacadeIIFE({
        mode: 'export',
        js: `${hasJs ? section.js : ''}${bindJS}`,
        sectionId: section.sectionId,
        exportId,
        documentScope: sectionUsesDocumentScope(section),
        idRenames: descendantRenames?.get(section.instanceId) || null,
        embedding
      })
    );
  }

  // The behaviour bundle goes FIRST: the prelude defines __pbDefine/__pbBindDoc,
  // each runtime registers itself, and only then do the per-instance facades
  // call in. Prepended here rather than in each of the four exporters, because a
  // caller that forgot would emit facades referencing undefined functions — and
  // only for stamped projects, the subset least likely to be in whatever export
  // someone happened to test.
  //
  // `assembleBehaviourJS` THROWS when a runtime was never loaded. That is the
  // contract: an export of a stamped widget with no runtime FAILS here, rather
  // than shipping a site carrying the markup of a working widget and none of its
  // behaviour. Entry points await `ensureBehaviourRuntimes(state)` first.
  const behaviourIds = [
    ...new Set(
      allInstances.flatMap((section) =>
        behaviourStampsInHtml(section.html)
          .filter((t) => behaviourIsShipped(t.id, t.variant))
          .map((t) => t.id)
      )
    )
  ];
  const bundle = behaviourIds.length ? assembleBehaviourJS(behaviourIds) : '';

  if (options.nativeScriptUnits) return bundle ? [bundle, ...scripts] : scripts;
  const body = scripts.join('\n\n');
  return bundle ? `${bundle}\n\n${body}` : body;
}

/**
 * Clean section HTML for export:
 * - Rewrite asset URLs using resolvedMap
 * - Set id attribute to exportId
 * - Apply element-level overrides (hidden, CSS, preserveAspectRatio)
 * - Strip all builder attributes
 * - Sanitize editable text content
 * - Apply form config if present
 */
export function cleanSectionHTML(section, exportId, resolvedMap, options = {}) {
  const stripAstroCid = options.stripAstroCid === true;
  let html = section.html || '';
  if (!html) return '';

  // Rewrite resolved asset URLs (use htmlPath for URL references, falls back to localPath)
  for (const [originalUrl, entry] of resolvedMap) {
    const replacePath = entry.htmlPath || entry.localPath;
    html = html.split(originalUrl).join(replacePath);
    // The parser can preserve source-rooted runtime attributes and inline CSS
    // (data-poster, data-placeholder, data-bg-*, style url()). The resolver
    // indexes those as absolute URLs so the vendored-store lookup works; map
    // the corresponding rooted spelling too before the export DOM is built.
    const sourceDomain = section.templateSiteName || options.sourceTemplateDomain;
    if (sourceDomain && /^https?:\/\//i.test(originalUrl)) {
      try {
        const parsed = new URL(originalUrl);
        if (normalizeDomain(parsed.host) === normalizeDomain(sourceDomain)) {
          const rooted = `${parsed.pathname}${parsed.search}${parsed.hash}`;
          html = html.split(rooted).join(replacePath);
        }
      } catch {
        // The absolute spelling above was still replaced; no rooted alias.
      }
    }
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  normalizeLogoSlots(root);

  // Set id on the main section element
  const sectionEl = root.firstElementChild;
  if (sectionEl) {
    sectionEl.setAttribute('id', exportId);
    // Canonical-id descendant renames (Workstream A2). No-op until the id
    // plan supplies a map (canonicalIds mode, off by default) — byte-neutral
    // for every current export. Runs before override application: overrides
    // key on data-pb-eid, which this never touches.
    if (options.idRenames instanceof Map && options.idRenames.size) {
      applyIdRenamesToSectionDOM(root, options.idRenames);
    }
    // Canonical-mode nav marker (A5b): the exported nav runtime and preview
    // toggles find the nav structurally via [data-pb-nav-root] once ids stop
    // being cs-navigation*. Never emitted in legacy mode (byte-neutral).
    if (options.navRootMarker === true) {
      sectionEl.setAttribute('data-pb-nav-root', '');
    }
    const siteName = templateSupportAttrValue(section.templateSiteName);
    const sourceName = templateSupportAttrValue(options.sourceTemplateDomain);
    if (
      siteName &&
      siteName !== sourceName &&
      sectionNeedsTemplateSupportCSS(section, options.sourceTemplateDomain)
    ) {
      sectionEl.setAttribute(TEMPLATE_SUPPORT_ATTR, siteName);
    }
  }

  // Engaged image-edit attrs are derived from current overrides. Strip any
  // stale copies from serialized section HTML before re-applying below.
  for (const img of root.querySelectorAll(
    'img[data-image-edit], img[data-image-hover], img[data-image-effect], img[data-image-dragging]'
  )) {
    img.removeAttribute('data-image-edit');
    img.removeAttribute('data-image-hover');
    img.removeAttribute('data-image-effect');
    img.removeAttribute('data-image-dragging');
  }

  const repairedElementCSS = hasRepairedElementCSS(section);
  if (repairedElementCSS) {
    root.innerHTML = applyPreviewElementAttributeOverrides(root.innerHTML, section);
  }

  // Responsive element overrides cannot be represented by inline styles.
  // Preserve a small export-only selector hook for their media-query rules.
  const responsiveEids = new Set(
    repairedElementCSS ? Object.keys(section.overrides?.elements || {}) : []
  );
  for (const bucket of Object.values(section.overrides?.responsive || {})) {
    for (const eid of Object.keys(bucket?.elements || {})) responsiveEids.add(eid);
  }
  for (const eid of responsiveEids) {
    const selector = `[data-pb-eid="${eid}"]`;
    if (repairedElementCSS) {
      for (const el of root.querySelectorAll(selector)) el.setAttribute('data-style-id', eid);
    } else {
      root.querySelector(selector)?.setAttribute('data-style-id', eid);
    }
  }

  // Apply element-level overrides before stripping builder attrs
  if (section.overrides && section.overrides.elements) {
    for (const [eid, props] of Object.entries(section.overrides.elements)) {
      const el = root.querySelector(`[data-pb-eid="${eid}"]`);
      if (!el) continue;

      if (props.hidden) {
        if (el.parentNode) el.remove();
        continue;
      }

      if (repairedElementCSS) continue;

      // imageHover is HTML-attribute-only; the image position keys are
      // handled below as CSS custom properties + engaged attribute, not
      // as plain camelCase→kebab CSS props.
      // paintedStyles is the format-painter snapshot — nested object,
      // emitted with !important so authored section CSS loses to the paint.
      // className/elementId are HTML attrs, __important is the Phase 6
      // escalation metadata map, and buttonType/buttonBgColor/fontSize are
      // translated below (mirroring css-assembler) — none are raw CSS props.
      const NON_CSS_FIELDS = new Set([
        // EA-7: entrance is MARKUP, never element CSS — this emitter appends
        // !important, and G-leak pins the field names out of emitted bytes.
        'revealPreset',
        'revealDelay',
        'hidden',
        'preserveAspectRatio',
        'imageTx',
        'imageTy',
        'imageScale',
        'imageHover',
        'imageEffect',
        'paintedStyles',
        'className',
        'elementId',
        '__important',
        'buttonType',
        'buttonBgColor',
        'fontSize'
      ]);
      // Properties that should be applied to the <picture> parent (not the <img>)
      // to work with CodeStitch's abs-positioned image layout pattern
      const PICTURE_PARENT_PROPS = new Set(['aspectRatio', 'borderRadius', 'boxShadow']);

      const imp = () => ' !important';

      const styleProps = [];
      const parentStyleProps = [];
      for (const [prop, val] of Object.entries(props)) {
        if (NON_CSS_FIELDS.has(prop)) continue;
        if (PICTURE_PARENT_PROPS.has(prop)) {
          parentStyleProps.push(`${camelToKebab(prop)}: ${val} !important`);
        } else {
          styleProps.push(`${camelToKebab(prop)}: ${val}${imp(prop)}`);
        }
      }

      // Button variant/color + fontSize records — mirror the preview
      // assembler's translation instead of leaking invalid raw props
      // (`button-type:`, `font-size: [object Object]`).
      if (props.buttonType === 'outline') {
        styleProps.push(
          'background: transparent !important',
          'border: 2px solid currentColor !important'
        );
      } else if (props.buttonType === 'ghost') {
        styleProps.push('background: transparent !important', 'border: none !important');
      }
      if (props.buttonBgColor) {
        const btnType = props.buttonType || 'solid';
        if (btnType === 'solid') {
          styleProps.push(`background-color: ${props.buttonBgColor}${imp('buttonBgColor')}`);
        } else {
          styleProps.push(`color: ${props.buttonBgColor}${imp('buttonBgColor')}`);
        }
      }
      if (props.fontSize != null && props.fontSize !== '') {
        const fontSizeVal = resolveFontSizeCSSValue(props.fontSize);
        if (fontSizeVal) styleProps.push(`font-size: ${fontSizeVal}${imp('fontSize')}`);
      }

      // Format-painter snapshot — nested computed-style object. Emit each
      // entry with !important on the inline style attribute so the painted
      // value beats both authored section CSS and any pseudo-class rules.
      if (props.paintedStyles && typeof props.paintedStyles === 'object') {
        for (const [sp, sv] of Object.entries(props.paintedStyles)) {
          if (typeof sv !== 'string' || sv === '') continue;
          styleProps.push(`${camelToKebab(sp)}: ${sv} !important`);
        }
      }

      // Image position overrides → CSS custom properties on the <img>.
      // The composed transform: rule lives in IMAGE_RUNTIME_CSS, gated by
      // the data-image-edit engaged attribute set below.
      if (el.tagName === 'IMG' || el.tagName === 'img') {
        const txN = Number(props.imageTx);
        const tyN = Number(props.imageTy);
        const scN = Number(props.imageScale);
        if (Number.isFinite(txN) && txN !== 0) {
          styleProps.push(`--pb-image-tx-pct: ${formatImagePct(txN)} !important`);
        }
        if (Number.isFinite(tyN) && tyN !== 0) {
          styleProps.push(`--pb-image-ty-pct: ${formatImagePct(tyN)} !important`);
        }
        if (Number.isFinite(scN) && scN !== 1) {
          styleProps.push(`--pb-image-scale: ${formatImageScale(scN)} !important`);
        }
      }

      // Apply picture-parent props to the <picture> wrapper (or the element itself if no <picture>)
      if (parentStyleProps.length) {
        parentStyleProps.push('overflow: hidden !important');
        // When aspect-ratio is set, the container needs to be in flow
        if (props.aspectRatio) {
          parentStyleProps.push('position: relative !important', 'height: auto !important');
        }
        const parentStyle = parentStyleProps.join('; ');
        const picture = el.closest('picture');
        const target = picture || el;
        const existing = target.getAttribute('style') || '';
        const sep = existing && !existing.trimEnd().endsWith(';') ? '; ' : ' ';
        target.setAttribute('style', (existing + sep + parentStyle).trim());

        // When aspect-ratio is set, override abs-positioning on the <img> so it takes effect
        if (props.aspectRatio && el.tagName === 'IMG') {
          styleProps.push(
            'position: relative !important',
            'width: 100% !important',
            'height: 100% !important'
          );
        }
      }

      if (styleProps.length) {
        const existing = el.getAttribute('style') || '';
        const sep = existing && !existing.trimEnd().endsWith(';') ? '; ' : ' ';
        el.setAttribute('style', (existing + sep + styleProps.join('; ')).trim());
      }

      if (props.preserveAspectRatio && (el.tagName === 'svg' || el.tagName === 'SVG')) {
        el.setAttribute('preserveAspectRatio', props.preserveAspectRatio);
      }

      // Engaged attributes for image position / hover preset. Set after
      // the CSS-var styles so the <img> has the data the IMAGE_RUNTIME_CSS
      // rules expect. These attribute names lack the `pb-` prefix so
      // stripAllBuilderAttrs() leaves them intact.
      if (el.tagName === 'IMG' || el.tagName === 'img') {
        if (
          hasImagePositionOverride(props) ||
          hasImageHoverOverride(props) ||
          hasImageEffectOverride(props)
        ) {
          el.setAttribute('data-image-edit', '');
        }
        if (hasImageHoverOverride(props) && isValidImageHoverPreset(props.imageHover)) {
          el.setAttribute('data-image-hover', props.imageHover);
        }
        if (hasImageEffectOverride(props) && isValidImageEffect(props.imageEffect)) {
          el.setAttribute('data-image-effect', props.imageEffect);
        }
      }
    }
  }

  // Builder entrance stamping (EA-2), BEFORE the strip — the strip's exact-name
  // carve-out is what lets the two attributes through, and stamping after it
  // would mean the clear-first pass had nothing to clear.
  //
  // `options.entrance` is supplied ONLY for PAGE sections. Nav and footer share
  // this helper, so the exclusion is enforced where the options are built
  // (exporter.js cleanOptsFor, the 11ty/Astro page-section calls,
  // preview-bundler.js) rather than by a check in here that a fourth caller
  // could forget to satisfy.
  //
  // ONE ENGINE PER SECTION (D8): a section whose markup already carries a
  // registered template reveal marker is not stamped while the template
  // convention is on, so the two never layer.
  if (options.entrance) {
    const sectionRoot = root.firstElementChild;
    if (sectionRoot) applyEntranceAttrs(sectionRoot, options.entrance);
  }

  // Strip all builder attributes
  stripAllBuilderAttrs(root);

  // 11ty export cleanup: remove Astro component scoping attributes.
  if (stripAstroCid) {
    const removeAstroAttrs = (el) => {
      if (!el?.attributes) return;
      const names = [];
      for (const attr of el.attributes) {
        if (attr.name.startsWith(ASTRO_CID_ATTR_PREFIX)) names.push(attr.name);
      }
      for (const name of names) {
        el.removeAttribute(name);
      }
    };
    removeAstroAttrs(root);
    for (const node of root.querySelectorAll('*')) {
      removeAstroAttrs(node);
    }
  }

  // Apply icon tinting (mask-image + transparent src swap) if enabled
  if (options.iconTinting !== false) {
    applyIconTintToDOM(root);
  }

  // Defense-in-depth: sanitize text content of editable elements
  for (const el of root.querySelectorAll(
    '.cs-topper, .cs-title, .cs-text, .cs-h3, .cs-item-p, .cs-item-text, .cs-name, .cs-job'
  )) {
    el.innerHTML = sanitize(el.innerHTML);
  }

  // Apply form config if present
  if (section.overrides?.formConfig) {
    applyFormConfig(doc, root, section.overrides.formConfig, {
      cloudflareForms: options.cloudflareForms === true
    });
  }

  return root.innerHTML;
}

/**
 * Apply form configuration to exported HTML.
 *
 * @param {object} [options]
 * @param {boolean} [options.cloudflareForms] The caller routes adoptable forms
 *   to /api/form and ships form-submit.js (the 11ty export with the forms
 *   feature on). The inline submit script is then skipped for exactly the forms
 *   that will be adopted, since two handlers on one form would double-post it.
 *   The success div is still emitted — form-submit.js reads its text for the
 *   confirmation message.
 */
export function applyFormConfig(doc, root, formConfig, options = {}) {
  const form = root.querySelector('form');
  if (!form) return;

  const { action, method, hiddenFields, endpoint, handleSubmit, successMessage } = formConfig;

  if (endpoint === 'formspree' && action) {
    form.setAttribute('action', `https://formspree.io/f/${action}`);
  } else if (action) {
    form.setAttribute('action', action);
  }

  if (method) form.setAttribute('method', method);

  if (endpoint === 'netlify') {
    form.setAttribute('data-netlify', 'true');
    if (action) form.setAttribute('name', action);
  }

  if (Array.isArray(hiddenFields)) {
    for (const { name, value } of hiddenFields) {
      if (!name) continue;
      const input = doc.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value || '';
      form.appendChild(input);
    }
  }

  if (handleSubmit && form.parentNode) {
    const successDiv = doc.createElement('div');
    successDiv.className = 'pb-form-success';
    successDiv.setAttribute('style', 'display:none');
    successDiv.textContent = successMessage || 'Thank you! Your submission has been received.';
    form.parentNode.insertBefore(successDiv, form.nextSibling);

    // Mirrors the adoption rule in convertFormsForCloudflare(): a Netlify
    // preset or an actionless form becomes an /api/form post, and those are
    // handled by form-submit.js. A Formspree or explicit third-party action is
    // left to this inline handler, since nothing else will bind to it.
    const finalAction = (form.getAttribute('action') || '').trim();
    const willUseCloudflareEndpoint =
      endpoint === 'netlify' || !finalAction || finalAction === '/api/form';
    if (options.cloudflareForms && willUseCloudflareEndpoint) return;

    const script = doc.createElement('script');
    script.textContent = `(function(){var f=document.currentScript.previousElementSibling.previousElementSibling;var s=document.currentScript.previousElementSibling;if(!f||f.tagName!=='FORM')return;f.addEventListener('submit',function(e){e.preventDefault();fetch(f.action,{method:f.method||'POST',body:new FormData(f)}).then(function(r){if(r.ok){f.style.display='none';s.style.display=''}else{alert('Something went wrong. Please try again.')}}).catch(function(){alert('Something went wrong. Please try again.')})})})();`;
    form.parentNode.insertBefore(script, successDiv.nextSibling);
  }
}

// ---------------------------------------------------------------------------
// Helpers (extracted from exporter.js)
// ---------------------------------------------------------------------------

export function escapeForScript(js) {
  return js.replace(/<\/script/gi, '<\\/script').replace(/<!--/g, '<\\!--');
}

export function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function camelToKebab(str) {
  return str.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
}

// ---------------------------------------------------------------------------
// Image attribute utilities (for 11ty export performance)
// ---------------------------------------------------------------------------

/**
 * The page's single LCP candidate, or null when the page has no images.
 *
 * ONE per page, by construction. `fetchpriority="high"` is a ranking, not a
 * boost: telling the browser that two images both matter most tells it nothing,
 * and the one that wins the race is whichever the preload scanner reaches
 * first — usually the nav logo, which is never the Largest Contentful Paint.
 *
 * Selection order:
 *   1. the first project-asset, NON-LOGO image in document order (the hero)
 *   2. failing that, the first logo — a logo-only page still has an LCP, and
 *      leaving it lazy costs the metric outright
 *
 * @param {string[]} sectionHTMLs cleaned section HTML, in page order
 * @param {Map} resolvedMap asset resolver output; only mapped images can use
 *   the optimized shortcode path
 * @returns {{sectionIndex: number, imgIndex: number, src: string, isLogo: boolean, isProjectAsset: boolean}|null}
 */
export function selectLcpImage(sectionHTMLs, resolvedMap, options = {}) {
  // `requireProjectAsset` defaults TRUE, which is the 11ty/Astro behaviour this
  // function was written for: those targets only care about an image they can
  // turn into an optimized shortcode, so a candidate with no resolved entry is
  // not a candidate. The FLAT target ships images as authored and has no
  // shortcode pipeline, so every resolved entry lacks `shortcodeSrc` and the
  // non-logo scan would always come up empty — it passes false.
  //
  // One selector with one documented difference, rather than a second copy of
  // the heuristic: two LCP selectors would be free to disagree about which
  // image matters, and disagreeing is worse than either answer.
  const requireProjectAsset = options.requireProjectAsset !== false;
  const parser = new DOMParser();
  const assetIndex = buildImageEntryIndex(resolvedMap);
  const scan = (wantLogo) => {
    for (let sectionIndex = 0; sectionIndex < sectionHTMLs.length; sectionIndex++) {
      const doc = parser.parseFromString(
        `<div>${sectionHTMLs[sectionIndex] || ''}</div>`,
        'text/html'
      );
      const imgs = [...doc.body.firstElementChild.querySelectorAll('img')];
      for (let imgIndex = 0; imgIndex < imgs.length; imgIndex++) {
        const img = imgs[imgIndex];
        if (isLogoImage(img) !== wantLogo) continue;
        const entry = wantLogo ? null : resolveImageEntry(img, assetIndex);
        if (!wantLogo && requireProjectAsset && !entry) continue;
        return {
          sectionIndex,
          imgIndex,
          src: img.getAttribute('src') || '',
          isLogo: wantLogo,
          isProjectAsset: !wantLogo && !!entry
        };
      }
    }
    return null;
  };
  return scan(false) || scan(true);
}

/**
 * Add loading/decoding/fetchpriority to every <img> in one section's HTML.
 *
 * Exactly one image on a page may be eager, and it is the one this section was
 * told about. `fetchpriority` is REMOVED from every other image rather than
 * merely not added — a section's source markup can carry its own, and two
 * high-priority images is the failure this function exists to prevent.
 *
 * @param {string} html section HTML
 * @param {boolean} isLcpSection does the page's LCP candidate live here
 * @param {number} [lcpImgIndex=0] which <img> in this section it is
 */
export function enhanceImgAttributes(html, isLcpSection, lcpImgIndex = 0) {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  const imgs = [...root.querySelectorAll('img')];

  for (let i = 0; i < imgs.length; i++) {
    const img = imgs[i];
    if (!img.hasAttribute('decoding')) {
      img.setAttribute('decoding', 'async');
    }

    if (isLcpSection && i === lcpImgIndex) {
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'high');
    } else {
      img.setAttribute('loading', 'lazy');
      img.removeAttribute('fetchpriority');
    }
  }

  return root.innerHTML;
}

/**
 * Remove builder entrance markers from the LCP image's ANCESTOR CHAIN (EA-2).
 *
 * The stamping engine already refuses to stamp a replaced element, so the
 * `<img>` itself is never a target. The hazard is its container: a staggered
 * `.cs-content` holding the hero image would start at `opacity: 0` with a
 * transform, and Largest Contentful Paint would not fire until the entrance
 * finished — turning a decorative animation into a measured regression on the
 * one metric the initiative promised not to move.
 *
 * Be precise about what this buys. It statically protects the image the
 * EXPORTER'S OWN heuristic selected (`selectLcpImage`). It does NOT protect a
 * page whose real LCP element is a heading or a background image; that case is
 * covered at runtime by the above-the-fold failsafe, which never arms anything
 * starting inside the first viewport.
 *
 * @param {string} html one section's HTML, already cleaned
 * @param {number} [lcpImgIndex=0] which `<img>` in this section is the candidate
 * @returns {string} the same HTML with the chain cleared
 */
export function stripEntranceFromLcpAncestors(html, lcpImgIndex = 0) {
  if (!html) return '';
  if (!html.includes(ENTRANCE_ATTR) && !html.includes(ENTRANCE_REVEALED_CLASS)) return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  const imgs = [...root.querySelectorAll('img')];
  const img = imgs[lcpImgIndex] || imgs[0];
  if (!img) return html;

  let changed = false;
  for (let node = img; node && node !== root.parentElement; node = node.parentElement) {
    if (!node.removeAttribute) continue;
    if (node.hasAttribute(ENTRANCE_ATTR)) {
      node.removeAttribute(ENTRANCE_ATTR);
      changed = true;
    }
    if (node.hasAttribute(ENTRANCE_DELAY_ATTR)) {
      node.removeAttribute(ENTRANCE_DELAY_ATTR);
      changed = true;
    }
    if (node.classList?.contains(ENTRANCE_REVEALED_CLASS)) {
      node.classList.remove(ENTRANCE_REVEALED_CLASS);
      if (node.getAttribute('class') === '') node.removeAttribute('class');
      changed = true;
    }
    if (node.classList?.contains(ENTRANCE_ARMED_CLASS)) {
      node.classList.remove(ENTRANCE_ARMED_CLASS);
      if (node.getAttribute('class') === '') node.removeAttribute('class');
      changed = true;
    }
  }
  return changed ? root.innerHTML : html;
}

/**
 * Extract the src of the first <img> in the HTML.
 * Used to populate the preloadImg frontmatter for LCP preloading.
 *
 * @param {string} html - Section HTML string
 * @returns {string} First img src, or empty string if none found
 */
export function extractFirstImgSrc(html) {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const img = doc.body.querySelector('img');
  return img ? img.getAttribute('src') || '' : '';
}

// ---------------------------------------------------------------------------
// 11ty export utilities (new)
// ---------------------------------------------------------------------------

/** Reserved slugs that conflict with 11ty/Cloudflare directory structure. */
const RESERVED_SLUGS = new Set([
  'admin',
  'assets',
  'css',
  'images',
  'api',
  'blog',
  'offline',
  'public',
  'functions',
  'pages',
  'shop',
  'shop-admin',
  'cart',
  'order-confirmation',
  'portal',
  'sign',
  'clients-admin',
  'messaging-admin',
  'contracts-admin',
  'invoicing-admin',
  'media-admin',
  'crm'
]);

/**
 * Escape a string for safe YAML frontmatter value.
 * Always wraps in double quotes, escapes internal quotes,
 * replaces newlines with spaces.
 */
export function safeYAMLValue(str) {
  if (str == null) return '""';
  const s = String(str)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, ' ')
    .replace(/\r/g, '');
  return `"${s}"`;
}

/**
 * Core slug computation — pure function, no mutation.
 * @param {string} name - Page name
 * @returns {string} Base slug (before collision resolution)
 */
export function slugify(name) {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'page'
  );
}

/**
 * Generate a URL-safe kebab-case slug from a page name.
 * Avoids collisions with reserved names and existing slugs.
 *
 * @param {string} name - Page name
 * @param {Set<string>} existingSlugs - Already-used slugs (mutated: new slug is added)
 * @param {Set<string>} [reserved] - Reserved names (defaults to RESERVED_SLUGS)
 * @returns {string} Unique slug
 */
export function generateSlug(name, existingSlugs, reserved = RESERVED_SLUGS) {
  const slug = slugify(name);

  // If slug matches a reserved name or already exists, append suffix
  let candidate = slug;
  let counter = 2;
  while (reserved.has(candidate) || existingSlugs.has(candidate)) {
    candidate = `${slug}-${counter}`;
    counter++;
  }

  existingSlugs.add(candidate);
  return candidate;
}

/**
 * Wrap content in {% raw %}...{% endraw %} to prevent Nunjucks parsing.
 * Handles content containing {% endraw %} by replacing it with a Nunjucks
 * expression that outputs the literal text, then wrapping the whole thing.
 * Skips wrapping entirely if content has no Nunjucks delimiters.
 *
 * @param {string} content
 * @returns {string}
 */
export function wrapInRawTags(content) {
  if (!content) return '';

  // If no Nunjucks delimiters, no wrapping needed
  if (!content.includes('{{') && !content.includes('{%')) {
    return content;
  }

  // If content contains {% endraw %}, we must handle it specially.
  // Strategy: close the raw block, use {{ "{% endraw %}" }} to emit
  // the literal text, then re-open raw for the remainder.
  // "{% endraw %}" → {% endraw %}{{ "{% endraw %}" }}{% raw %}
  if (content.includes('{% endraw %}')) {
    const safe = content.split('{% endraw %}').join('{% endraw %}{{ "{% endraw %}" }}{% raw %}');
    return `{% raw %}${safe}{% endraw %}`;
  }

  return `{% raw %}${content}{% endraw %}`;
}

// ============================================================
// Structured data — JSON-LD (Polish Phase 14)
// ============================================================

function _sdClean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Wrap a JSON-LD data object in a script tag. `<` is escaped to < inside
 * the JSON so user strings can never break out of the script element.
 * @param {object|null} data
 * @returns {string} script tag, or '' when data is falsy
 */
export function jsonLdScript(data) {
  if (!data) return '';
  return `<script type="application/ld+json">${JSON.stringify(data).replace(/</g, '\\u003c')}</script>`;
}

/**
 * Build a schema.org LocalBusiness object from clientInfo. Returns null unless
 * there is a name plus at least one real business signal (phone, email, domain,
 * or street address) — an empty stub would be structured-data noise.
 * @param {object} clientInfo - state.clientInfo shape (client-info.js)
 * @returns {object|null}
 */
export function buildLocalBusinessJsonLd(clientInfo = {}) {
  const name = _sdClean(clientInfo.name);
  const phone = _sdClean(clientInfo.phoneFormatted) || _sdClean(clientInfo.phone);
  const email = _sdClean(clientInfo.email);
  const domain = normalizeDomain(clientInfo.domain);
  const address = clientInfo.address || {};
  const street = [_sdClean(address.lineOne), _sdClean(address.lineTwo)].filter(Boolean).join(', ');
  if (!name || !(phone || email || domain || street)) return null;

  const data = { '@context': 'https://schema.org', '@type': 'LocalBusiness', name };
  const tagline = _sdClean(clientInfo.tagline);
  if (tagline) data.description = tagline;
  if (domain) data.url = `https://${domain}/`;
  if (phone) data.telephone = phone;
  if (email) data.email = email;
  const city = _sdClean(address.city);
  const county = _sdClean(address.county);
  const postcode = _sdClean(address.postcode);
  if (street || city || postcode) {
    data.address = { '@type': 'PostalAddress' };
    if (street) data.address.streetAddress = street;
    if (city) data.address.addressLocality = city;
    if (county) data.address.addressRegion = county;
    if (postcode) data.address.postalCode = postcode;
  }
  const sameAs = Object.values(clientInfo.social || {})
    .map(_sdClean)
    .filter(Boolean);
  if (sameAs.length) data.sameAs = sameAs;
  return data;
}

function _sdText(el) {
  return (el?.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Extract question/answer pairs from a CodeStitch-style FAQ section
 * (`.cs-faq-item` items: question in `.cs-button-text`/button, answer in
 * `.cs-item-p`/remaining paragraphs). Unknown markup yields [] — FAQ JSON-LD
 * is simply skipped rather than guessed.
 * @param {string} sectionHTML
 * @returns {Array<{question: string, answer: string}>}
 */
export function extractFaqPairs(sectionHTML) {
  if (!sectionHTML || !sectionHTML.includes('cs-faq-item')) return [];
  let doc;
  try {
    doc = new DOMParser().parseFromString(
      `<div data-pb-faq-root>${sectionHTML}</div>`,
      'text/html'
    );
  } catch {
    return [];
  }
  const pairs = [];
  for (const item of doc.querySelectorAll('.cs-faq-item')) {
    const question =
      _sdText(item.querySelector('.cs-button-text')) ||
      _sdText(item.querySelector('button')) ||
      _sdText(item.querySelector('h3, h4, h5'));
    let answer = _sdText(item.querySelector('.cs-item-p'));
    if (!answer) {
      answer = [...item.querySelectorAll('p')]
        .filter((p) => !p.closest('button'))
        .map(_sdText)
        .filter(Boolean)
        .join(' ');
    }
    if (question && answer) pairs.push({ question, answer });
  }
  return pairs;
}

/**
 * Build a schema.org FAQPage object from extracted pairs.
 * @param {Array<{question: string, answer: string}>} pairs
 * @returns {object|null} null when there are no pairs
 */
export function buildFaqPageJsonLd(pairs) {
  if (!Array.isArray(pairs) || pairs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: pairs.map((pair) => ({
      '@type': 'Question',
      name: pair.question,
      acceptedAnswer: { '@type': 'Answer', text: pair.answer }
    }))
  };
}
